/**
 * Per-thread chat-input draft persisted in localStorage. Lets a user navigate
 * away from a chat (e.g. to peek at a doc) and come back with their unsent
 * message still in the textarea.
 *
 * Keyed by (projectId, threadKey) where threadKey is the chat thread id, or
 * the literal "new" for the landing page that creates a thread on send.
 *
 * All storage access is wrapped in try/catch — SSR, private-mode, and quota
 * errors should never bubble out to the caller. Reads fall back to '', writes
 * silently no-op.
 */

const CONTENT_PREFIX = 'hub:chatDraft:v1:'
const META_PREFIX = 'hub:chatDraft:v1:meta:'
// v3: the v1/v2 attachment drafts were written by an effect that observed
// stale state during thread switches, leaking the same files into every
// thread's key. Those generations are ignored and swept by
// pruneOldChatDrafts.
const FILES_PREFIX = 'hub:chatDraft:v3:files:'
const LEGACY_FILES_PREFIXES = ['hub:chatDraft:v1:files:', 'hub:chatDraft:v2:files:']
const MODE_PREFIX = 'hub:chatDraft:v1:mode:'
const SELECTIONS_PREFIX = 'hub:chatDraft:v1:selections:'

interface DraftMeta {
  updatedAt: number
}

function contentKey(projectId: string, threadKey: string): string {
  return `${CONTENT_PREFIX}${projectId}:${threadKey}`
}

function metaKey(projectId: string, threadKey: string): string {
  return `${META_PREFIX}${projectId}:${threadKey}`
}

function filesKey(projectId: string, threadKey: string): string {
  return `${FILES_PREFIX}${projectId}:${threadKey}`
}

function modeKey(projectId: string, threadKey: string): string {
  return `${MODE_PREFIX}${projectId}:${threadKey}`
}

function selectionsKey(projectId: string, threadKey: string): string {
  return `${SELECTIONS_PREFIX}${projectId}:${threadKey}`
}

function touchMeta(store: Storage, projectId: string, threadKey: string): void {
  const meta: DraftMeta = { updatedAt: Date.now() }
  store.setItem(metaKey(projectId, threadKey), JSON.stringify(meta))
}

/** Drop the meta row once neither text, attachments nor selections remain. */
function cleanupMetaIfEmpty(store: Storage, projectId: string, threadKey: string): void {
  if (
    store.getItem(contentKey(projectId, threadKey)) === null &&
    store.getItem(filesKey(projectId, threadKey)) === null &&
    store.getItem(modeKey(projectId, threadKey)) === null &&
    store.getItem(selectionsKey(projectId, threadKey)) === null
  ) {
    store.removeItem(metaKey(projectId, threadKey))
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

export function getChatDraft(projectId: string, threadKey: string): string {
  const store = safeStorage()
  if (!store) return ''
  try {
    return store.getItem(contentKey(projectId, threadKey)) ?? ''
  } catch {
    return ''
  }
}

export function setChatDraft(projectId: string, threadKey: string, value: string): void {
  const store = safeStorage()
  if (!store) return
  try {
    if (value === '') {
      // Treat empty text as "no text draft" — attachments may still be held.
      store.removeItem(contentKey(projectId, threadKey))
      cleanupMetaIfEmpty(store, projectId, threadKey)
      return
    }
    store.setItem(contentKey(projectId, threadKey), value)
    touchMeta(store, projectId, threadKey)
  } catch {
    // Quota exceeded / serialization error — drop silently.
  }
}

/**
 * Signal that a thread's draft was written back from outside the composer —
 * a send the server refused. The composer only reads the store on mount and
 * on a thread switch, so it needs to be told.
 */
export const CHAT_DRAFT_RESTORED_EVENT = 'chat-draft-restored'

export interface ChatDraftRestoredDetail {
  projectId: string
  threadKey: string
}

/** Put text back into a thread's composer and make it re-read the draft. */
export function restoreChatDraft(projectId: string, threadKey: string, value: string): void {
  setChatDraft(projectId, threadKey, value)
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent<ChatDraftRestoredDetail>(CHAT_DRAFT_RESTORED_EVENT, {
      detail: { projectId, threadKey },
    })
  )
}

export function clearChatDraft(projectId: string, threadKey: string): void {
  const store = safeStorage()
  if (!store) return
  try {
    store.removeItem(contentKey(projectId, threadKey))
    store.removeItem(filesKey(projectId, threadKey))
    store.removeItem(modeKey(projectId, threadKey))
    store.removeItem(selectionsKey(projectId, threadKey))
    store.removeItem(metaKey(projectId, threadKey))
  } catch {
    // ignore
  }
}

/**
 * Model mode chosen while composing in a thread. Part of the draft: cleared
 * on send and by pruning, so switching to a chat without a saved draft mode
 * starts back at Normal.
 */
export function getChatDraftMode(projectId: string, threadKey: string): string | null {
  const store = safeStorage()
  if (!store) return null
  try {
    return store.getItem(modeKey(projectId, threadKey))
  } catch {
    return null
  }
}

export function setChatDraftMode(projectId: string, threadKey: string, mode: string): void {
  const store = safeStorage()
  if (!store) return
  try {
    if (mode === 'normal') {
      // Normal is the default — no need to keep a row for it.
      store.removeItem(modeKey(projectId, threadKey))
      cleanupMetaIfEmpty(store, projectId, threadKey)
      return
    }
    store.setItem(modeKey(projectId, threadKey), mode)
    touchMeta(store, projectId, threadKey)
  } catch {
    // ignore
  }
}

/**
 * Attachments already uploaded from the composer but not yet sent. Stored as
 * opaque JSON (the caller's ChatAttachment shape) next to the text draft and
 * pruned/cleared together with it.
 */
export function getChatDraftAttachments<T>(projectId: string, threadKey: string): T[] {
  const store = safeStorage()
  if (!store) return []
  try {
    const raw = store.getItem(filesKey(projectId, threadKey))
    const parsed = raw ? (JSON.parse(raw) as unknown) : null
    return Array.isArray(parsed) ? (parsed as T[]) : []
  } catch {
    return []
  }
}

export function setChatDraftAttachments(
  projectId: string,
  threadKey: string,
  attachments: unknown[]
): void {
  const store = safeStorage()
  if (!store) return
  try {
    if (attachments.length === 0) {
      store.removeItem(filesKey(projectId, threadKey))
      cleanupMetaIfEmpty(store, projectId, threadKey)
      return
    }
    store.setItem(filesKey(projectId, threadKey), JSON.stringify(attachments))
    touchMeta(store, projectId, threadKey)
  } catch {
    // Quota exceeded / serialization error — drop silently.
  }
}

/**
 * Text selected in a reply and added to the composer ("Add to chat") but not
 * yet sent. Opaque JSON like the attachments (the caller's ChatSelection
 * shape), pruned/cleared together with the text draft.
 */
export function getChatDraftSelections(projectId: string, threadKey: string): unknown {
  const store = safeStorage()
  if (!store) return []
  try {
    const raw = store.getItem(selectionsKey(projectId, threadKey))
    return raw ? (JSON.parse(raw) as unknown) : []
  } catch {
    return []
  }
}

export function setChatDraftSelections(
  projectId: string,
  threadKey: string,
  selections: unknown[]
): void {
  const store = safeStorage()
  if (!store) return
  try {
    if (selections.length === 0) {
      store.removeItem(selectionsKey(projectId, threadKey))
      cleanupMetaIfEmpty(store, projectId, threadKey)
      return
    }
    store.setItem(selectionsKey(projectId, threadKey), JSON.stringify(selections))
    touchMeta(store, projectId, threadKey)
  } catch {
    // Quota exceeded / serialization error — drop silently.
  }
}

/**
 * Signal that a selection was added to a thread's draft from outside the
 * composer (the floating "Add to chat" menu over a reply), so the composer
 * re-reads its selections.
 */
export const CHAT_DRAFT_SELECTIONS_EVENT = 'chat-draft-selections-changed'

export function notifyChatDraftSelectionsChanged(projectId: string, threadKey: string): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent<ChatDraftRestoredDetail>(CHAT_DRAFT_SELECTIONS_EVENT, {
      detail: { projectId, threadKey },
    })
  )
}

/**
 * Walk localStorage and drop any draft whose meta.updatedAt is older than
 * `maxAgeMs`. Missing or unparseable meta is treated as expired so a stuck
 * row from a previous schema can't linger forever.
 */
export function pruneOldChatDrafts(maxAgeMs: number): void {
  const store = safeStorage()
  if (!store) return
  const cutoff = Date.now() - maxAgeMs
  const toRemove: string[] = []
  const legacy: string[] = []
  try {
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i)
      if (key && LEGACY_FILES_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        legacy.push(key)
        continue
      }
      if (!key || !key.startsWith(META_PREFIX)) continue
      let updatedAt = 0
      try {
        const raw = store.getItem(key)
        if (raw) {
          const parsed = JSON.parse(raw) as { updatedAt?: unknown }
          if (typeof parsed.updatedAt === 'number') updatedAt = parsed.updatedAt
        }
      } catch {
        // Unparseable → treat as old.
      }
      if (updatedAt < cutoff) toRemove.push(key)
    }
  } catch {
    // Couldn't iterate — bail.
    return
  }
  for (const metaK of toRemove) {
    try {
      const suffix = metaK.slice(META_PREFIX.length)
      store.removeItem(CONTENT_PREFIX + suffix)
      store.removeItem(FILES_PREFIX + suffix)
      store.removeItem(MODE_PREFIX + suffix)
      store.removeItem(SELECTIONS_PREFIX + suffix)
      store.removeItem(metaK)
    } catch {
      // ignore individual removal failures
    }
  }
  for (const key of legacy) {
    try {
      store.removeItem(key)
    } catch {
      // ignore
    }
  }
}
